import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-fd5f2ac0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "abilities-radar-graphic__toolbar mb-4" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "bi bi-funnel p-0" }, null, -1))
const _hoisted_4 = [
  _hoisted_3
]
const _hoisted_5 = {
  key: 0,
  class: "abilities-radar-graphic__filter-panel"
}
const _hoisted_6 = { class: "abilities-radar-graphic__filter-head" }
const _hoisted_7 = { class: "abilities-radar-graphic__filter-label mb-1" }
const _hoisted_8 = { class: "abilities-radar-graphic__filter-help mb-0" }
const _hoisted_9 = { class: "abilities-radar-graphic__count-pill" }
const _hoisted_10 = {
  key: 0,
  class: "abilities-radar-graphic__filter-error"
}
const _hoisted_11 = { class: "abilities-radar-graphic__filter-actions" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = ["disabled"]
const _hoisted_14 = {
  key: 1,
  class: "abilities-radar-graphic__state"
}
const _hoisted_15 = {
  key: 2,
  class: "abilities-radar-graphic__chart"
}
const _hoisted_16 = {
  key: 3,
  class: "abilities-radar-graphic__state abilities-radar-graphic__state--empty"
}
const _hoisted_17 = {
  key: 4,
  class: "abilities-radar-graphic__overlay"
}

import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Multiselect from "@vueform/multiselect";
import LoadingSpinner from "@/modern/components/ui/LoadingSpinner.vue";
import type { ModernCategory } from "@/modern/types/dashboard";


export default /*#__PURE__*/_defineComponent({
  props: {
    categoriesData: null,
    allCategoriesData: { default: () => [] },
    selectedCategoryIds: { default: () => [] },
    filtering: { type: Boolean, default: false },
    currentLanguage: null,
    loading: { type: Boolean, default: false },
    height: { default: 270 },
    seriesName: { default: "Percentage" },
    color: { default: "#7525ee" },
    loadingLabel: { default: "Loading..." },
    emptyLabel: { default: "No data available" }
  },
  emits: ["update:selectedCategoryIds", "apply-filter"],
  setup(__props: any, { emit }: { emit: ({
	(e: "update:selectedCategoryIds", value: number[]): void;
	(e: "apply-filter"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { categoriesData: ModernCategory[], allCategoriesData: ModernCategory[], selectedCategoryIds: number[], filtering: boolean, currentLanguage: string, loading: boolean, height: number | string, seriesName: string, color: string, loadingLabel: string, emptyLabel: string }





const { t } = useI18n();
const selectedCategories = ref<number[]>([]);
const showFilterMenu = ref(false);
const multiselect = ref<null | { close: () => void }>(null);

const categoryOptions = computed(() =>
	(props.allCategoriesData || []).map((category) => ({ value: category.id, label: category.name }))
);
const canApplyFilter = computed(() => selectedCategories.value.length === 5);
const chartCategories = computed(() => props.categoriesData.map((category) => category.name));
const chartValues = computed(() => props.categoriesData.map((category) => Number.parseFloat(category.percentage) || 0));
const hasData = computed(() => chartCategories.value.length > 0 && chartValues.value.length > 0);
const isUpdating = computed(() => Boolean(props.filtering));

watch(
	selectedCategories,
	(newValue) => {
		emit("update:selectedCategoryIds", [...newValue]);
		if (newValue.length === 5) {
			multiselect.value?.close();
		}
	},
	{ immediate: true }
);

watch(
	() => props.selectedCategoryIds,
	(nextSelectedIds) => {
		const next = nextSelectedIds || [];
		const current = selectedCategories.value;
		if (
			next.length === current.length &&
			next.every((id, i) => id === current[i])
		) {
			return;
		}
		selectedCategories.value = [...next];
	},
	{ immediate: true }
);

const toggleFilterMenu = () => {
	if (props.loading || props.filtering) {
		return;
	}

	showFilterMenu.value = !showFilterMenu.value;
};

const applyFilter = () => {
	if (!canApplyFilter.value) {
		return;
	}

	emit("apply-filter");
	showFilterMenu.value = false;
};

const normalizedHeight = computed(() => {
	if (typeof props.height === "number") {
		return props.height;
	}
	const parsed = Number.parseInt(String(props.height), 10);
	return Number.isNaN(parsed) ? 270 : parsed;
});
const radarSize = computed(() => Math.max(110, Math.min(180, Math.round(normalizedHeight.value * 0.35))));

const chartOptions = computed(() => ({
	chart: {
		height: props.height,
		type: "radar",
		background: "transparent",
		toolbar: { show: false },
	},
	yaxis: {
		stepSize: 20,
		forceNiceScale: true,
		floating: false,
		max: 99,
		labels: {
			formatter: (value: number) => `${value}`,
		},
	},
	xaxis: {
		categories: chartCategories.value,
		forceNiceScale: true,
		floating: false,
	},
	theme: { mode: "dark" },
	plotOptions: { radar: { size: radarSize.value } },
	colors: [props.color],
	fill: { opacity: 0.8 },
	markers: { size: 5, hover: { size: 7 } },
}));

const series = computed(() => [
	{
		name: props.seriesName,
		data: chartValues.value,
	},
]);

const chartKey = computed(() => `${chartCategories.value.join("|")}:${chartValues.value.join("|")}`);

return (_ctx: any,_cache: any) => {
  const _component_apexchart = _resolveComponent("apexchart")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["abilities-radar-graphic", { 'is-updating': _unref(isUpdating) }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("button", {
        class: _normalizeClass(["btn btn-secondary abilities-radar-graphic__filter-button d-inline-flex justify-content-center align-items-center", { 'is-active': showFilterMenu.value }]),
        disabled: __props.loading || __props.filtering,
        onClick: toggleFilterMenu
      }, _hoisted_4, 10, _hoisted_2)
    ]),
    (showFilterMenu.value)
      ? (_openBlock(), _createElementBlock("aside", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", null, [
              _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)(`${__props.currentLanguage}.common.select_categories`)), 1),
              _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(t)(`${__props.currentLanguage}.common.select_5_categories`)), 1)
            ]),
            _createElementVNode("span", _hoisted_9, _toDisplayString(selectedCategories.value.length) + "/5", 1)
          ]),
          _createVNode(_unref(Multiselect), {
            modelValue: selectedCategories.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedCategories).value = $event)),
            options: _unref(categoryOptions),
            mode: "multiple",
            "clear-on-select": false,
            "close-on-select": false,
            disabled: __props.loading || __props.filtering,
            searchable: true,
            max: 5,
            placeholder: _unref(t)(`${__props.currentLanguage}.common.select_categories`),
            label: "label",
            "track-by": "value",
            class: "abilities-radar-graphic__multiselect",
            ref_key: "multiselect",
            ref: multiselect
          }, null, 8, ["modelValue", "options", "disabled", "placeholder"]),
          (!_unref(canApplyFilter))
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(_unref(t)(`${__props.currentLanguage}.common.select_5_categories`)), 1))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("button", {
              class: "btn btn-light",
              disabled: __props.filtering,
              onClick: _cache[1] || (_cache[1] = ($event: any) => (showFilterMenu.value = false))
            }, _toDisplayString(_unref(t)(`${__props.currentLanguage}.common.close`)), 9, _hoisted_12),
            _createElementVNode("button", {
              class: "btn btn-primary",
              disabled: !_unref(canApplyFilter) || __props.filtering,
              onClick: applyFilter
            }, _toDisplayString(_unref(t)(`${__props.currentLanguage}.common.apply_filter`)), 9, _hoisted_13)
          ])
        ]))
      : _createCommentVNode("", true),
    (__props.loading && !_unref(hasData))
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createVNode(LoadingSpinner, {
            label: __props.loadingLabel,
            size: "sm"
          }, null, 8, ["label"])
        ]))
      : (_unref(hasData))
        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
            (_openBlock(), _createBlock(_component_apexchart, {
              key: _unref(chartKey),
              class: "mixed-widget-2-chart card-rounded-bottom",
              options: _unref(chartOptions),
              series: _unref(series),
              height: __props.height,
              width: 320,
              type: "radar"
            }, null, 8, ["options", "series", "height"]))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_16, [
            _createElementVNode("span", null, _toDisplayString(__props.emptyLabel), 1)
          ])),
    (_unref(isUpdating) && _unref(hasData))
      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
          _createVNode(LoadingSpinner, {
            label: __props.loadingLabel,
            size: "sm"
          }, null, 8, ["label"])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})